import { groupBy, orderBy } from 'lodash';
import { getEventTypeByTargetBucket } from './flow-report-utils';

export const getFlowChildGroups = (bucket, groups) => {
  if (groups.length === 0) {
    return [];
  }
  //remove first element from group and process it
  const currentGroup = groups.splice(0, 1)[0];

  const currentGroupBucket = bucket[currentGroup];

  //could not find current group or there are no chilc buckets on it, exit
  if (!currentGroupBucket || !Array.isArray(currentGroupBucket.buckets)) {
    return [];
  }

  const rows = [];
  currentGroupBucket.buckets.forEach(cgb => {
    //build row and append to array of rows
    let allRow = {
      [currentGroup]: cgb.key,
      flowViews: cgb.doc_count
    };

    groups.forEach(g => {
      allRow[g] = 'All';
    });
    if (groups.length > 0) {
      allRow.isAllRow = true;
    }

    rows.push(allRow);

    const childGroups = getFlowChildGroups(cgb, [...groups]);
    childGroups.forEach(cg => {
      cg[currentGroup] = cgb.key;
    });

    rows.push(...childGroups);
  });
  return rows;
};

const getGroupedEventValue = (dayBucket, eventType, targetType, eventGroup) => {
  const targetBucket = getEventTypeByTargetBucket(
    dayBucket,
    eventType,
    targetType
  );
  if (targetBucket) {
    const matchingGroupBucket = findMatchingGroupBucket(
      targetBucket,
      eventGroup
    );
    if (matchingGroupBucket) {
      return matchingGroupBucket.doc_count;
    }
    return 0;
  }
  return 0;
};

const getGroupedByTargetIdEventValue = (
  dayBucket,
  eventType,
  targetType,
  eventGroup
) => {
  const targetBucket = getEventTypeByTargetBucket(
    dayBucket,
    eventType,
    targetType
  );
  if (targetBucket) {
    const countsById = {};
    targetBucket.targetId.pageAndFormTargets.buckets.forEach(b => {
      //find the matching event group, then use that value
      const matchingGroupBucket = findMatchingGroupBucket(b, eventGroup);

      countsById[`${targetType}.${b.key}`] = matchingGroupBucket
        ? matchingGroupBucket.doc_count
        : 0; // b.doc_count;
    });
    return countsById;
  }
};

const getGroupedOfferClicksByPageId = (dayBucket, eventGroup) => {
  if (
    dayBucket &&
    dayBucket.offers &&
    Array.isArray(dayBucket.offers.offerEventsBySourceId.buckets)
  ) {
    const clicksByPageId = {};
    dayBucket.offers.offerEventsBySourceId.buckets.forEach(b => {
      const matchingGroupBucket = findMatchingGroupBucket(b.clicks, eventGroup);

      clicksByPageId[`offerClicks.${b.key}`] = matchingGroupBucket
        ? matchingGroupBucket.offerClicks.value
        : 0;
    });
    return clicksByPageId;
  }
  return {};
};

const getGroupedTotalOfferRevenue = (dayBucket, eventGroup) => {
  return (
    dayBucket &&
    dayBucket.offers &&
    dayBucket.offers.offerEventsBySourceId.buckets
      .map(b => {
        const matchingBucket = findMatchingGroupBucket(b, eventGroup);
        if (matchingBucket) {
          return matchingBucket.revenue.value;
        }
        return 0;
      })
      .reduce((a, b) => a + b, 0)
  );
};

const getGroupedCompletedSurveys = (dayBucket, eventGroup) => {
  if (dayBucket && dayBucket.surveys) {
    const matchingGroupBucket = findMatchingGroupBucket(
      dayBucket.surveys,
      eventGroup
    );
    if (matchingGroupBucket) {
      return matchingGroupBucket.completedSurveys.value;
    }
    return 0;
  }
};

const getGroupedGdprGrants = (dayBucket, eventGroup) => {
  if (dayBucket && dayBucket.consents) {
    const matchingGroupBucket = findMatchingGroupBucket(
      dayBucket.consents,
      eventGroup
    );
    if (matchingGroupBucket) {
      return matchingGroupBucket.gdprGrants.value;
    }
    return 0;
  }
};

const getGroupedFullRegistrations = (dayBucket, eventGroup) => {
  if (dayBucket && dayBucket.registrations) {
    const matchingGroupBucket = findMatchingGroupBucket(
      dayBucket.registrations,
      eventGroup
    );
    if (matchingGroupBucket) {
      return matchingGroupBucket.fullRegistrations.value;
    }
    return 0;
  }
};

const getGroupedOfferRevenueByPageId = (dayBucket, eventGroup) => {
  if (
    dayBucket &&
    dayBucket.offers &&
    Array.isArray(dayBucket.offers.offerEventsBySourceId.buckets)
  ) {
    const revenueByPageId = {};
    dayBucket.offers.offerEventsBySourceId.buckets.forEach(b => {
      const matchingGroupBucket = findMatchingGroupBucket(b, eventGroup);

      revenueByPageId[`pageRevenue.${b.key}`] = matchingGroupBucket
        ? matchingGroupBucket.revenue.value
        : 0;
    });
    return revenueByPageId;
  }
  return {};
};

const getGroupedPayableConversionsByPageId = (dayBucket, eventGroup) => {
  if (
    dayBucket &&
    dayBucket.offers &&
    Array.isArray(dayBucket.offers.offerEventsBySourceId.buckets)
  ) {
    const payableConversionsByPageId = {};
    dayBucket.offers.offerEventsBySourceId.buckets.forEach(b => {
      const matchingGroupBucket = findMatchingGroupBucket(
        b.payableConversions,
        eventGroup
      );
      payableConversionsByPageId[
        `payableConversions.${b.key}`
      ] = matchingGroupBucket ? matchingGroupBucket.offerConversions.value : 0;
    });
    return payableConversionsByPageId;
  }
  return {};
};

export const findMatchingGroupBucket = (parentBucket, eventGroup) => {
  if (parentBucket && Array.isArray(eventGroup) && eventGroup.length > 0) {
    const eventGroupCopy = [...eventGroup];
    const currentGroup = eventGroupCopy.splice(0, 1)[0];
    if (
      parentBucket[currentGroup.key] &&
      Array.isArray(parentBucket[currentGroup.key].buckets)
    ) {
      if (currentGroup.value === 'All') {
        return parentBucket;
      }
      const matchingChildBucket = parentBucket[currentGroup.key].buckets.find(
        b => b.key === currentGroup.value
      );
      if (matchingChildBucket && eventGroupCopy.length === 0) {
        return matchingChildBucket;
      } else if (matchingChildBucket) {
        return findMatchingGroupBucket(matchingChildBucket, eventGroupCopy);
      }
    }
  }
};

export const getGroupRows = (dayBucket, groups) => {
  let childGroupRows = [];

  const flowBucketsById = getEventTypeByTargetBucket(dayBucket, 'view', 'flow');
  if (
    flowBucketsById &&
    flowBucketsById.targetId &&
    Array.isArray(flowBucketsById.targetId.pageAndFormTargets.buckets)
  ) {
    const flowBucket = flowBucketsById.targetId.pageAndFormTargets.buckets[0];

    childGroupRows = getFlowChildGroups(flowBucket, [...groups]);

    childGroupRows.forEach(cgr => {
      const eventGroup = groups.map(g => {
        return {
          key: g,
          value: cgr[g]
        };
      });

      cgr.date = dayBucket.key_as_string;

      Object.assign(
        cgr,
        getGroupedByTargetIdEventValue(dayBucket, 'view', 'page', eventGroup)
      );
      Object.assign(
        cgr,
        getGroupedByTargetIdEventValue(dayBucket, 'submit', 'form', eventGroup)
      );
      Object.assign(cgr, getGroupedOfferRevenueByPageId(dayBucket, eventGroup));
      Object.assign(cgr, getGroupedOfferClicksByPageId(dayBucket, eventGroup));
      Object.assign(
        cgr,
        getGroupedPayableConversionsByPageId(dayBucket, eventGroup)
      );

      cgr.offerClicks = getGroupedEventValue(
        dayBucket,
        'click',
        'offer',
        eventGroup
      );
      cgr.surveyViews = getGroupedEventValue(
        dayBucket,
        'view',
        'survey',
        'eventGroup'
      );
      cgr.completedSurveys = getGroupedCompletedSurveys(dayBucket, eventGroup);
      cgr.totalRevenue = getGroupedTotalOfferRevenue(dayBucket, eventGroup);
      cgr.consents = getGroupedGdprGrants(dayBucket, eventGroup);
      cgr.fullRegs = getGroupedFullRegistrations(dayBucket, eventGroup);
    });
  }

  return childGroupRows;
};

const getMinOrMaxSortValue = (dataType, direction, isMax) => {
  let _dir = direction;

  //want this element to always sort at the end
  if (isMax) {
    if (_dir === 'asc') {
      _dir = 'desc';
    } else {
      _dir = 'asc';
    }
  }

  if (dataType === 'number') {
    return _dir === 'desc' ? 9007199254740991 : -9007199254740991; //MAX_SAFE_INTEGER
  } else {
    return _dir === 'desc' ? '~~~~~' : '!!!!!';
  }
};

/**
 * returns [{key:xxx, children:[]}]
 * @param {*} reportData
 * @param {*} groupProperties
 * @param {*} columnSort
 */
export const getSortGroups = (reportData, groupProperties, columnSort) => {
  const result = [];
  if (groupProperties.length === 0) {
    return result;
  }

  const groupSortIndex = groupProperties.indexOf(
    columnSort.field.split('.')[0]
  );

  const currentGroupProperty = groupProperties.splice(0, 1)[0];

  const groupedData = groupBy(reportData, currentGroupProperty);

  const alphabeticallySortedKeys = orderBy(
    Object.keys(groupedData),
    k => k.toLowerCase(),
    ['desc']
  );
  const sortedGroupKeys = orderBy(
    alphabeticallySortedKeys,
    key => {
      const sortFieldType =
        groupedData[key][0] && typeof groupedData[key][0][columnSort.field];

      if (key === 'All') {
        return getMinOrMaxSortValue(sortFieldType, columnSort.direction);
      } else if (key === 'No value') {
        return getMinOrMaxSortValue(sortFieldType, columnSort.direction, true);
      } else {
        let sortRow;

        //if we are performing a group sort on a nested field, sort this level based on that nested field
        if (groupSortIndex > 0) {
          const orderedChildData = orderBy(
            groupedData[key].filter(gd => {
              const keyParts = gd.key.split('.');
              //ignore aggregate and no value rows when getting nested sort
              return (
                keyParts.indexOf('All') === -1 &&
                keyParts.indexOf('No value') === -1
              );
            }),
            childRow => childRow[columnSort.field].toLowerCase(),
            [columnSort.direction]
          );
          //we are sorting by a field that doesn't exist in this group, default it to the end - right before No Value
          if (typeof orderedChildData[0] === 'undefined') {
            return getMinOrMaxSortValue(
              sortFieldType,
              columnSort.direction,
              true
            );
          }

          sortRow = orderedChildData[0];
        } else if (groupProperties.length > 0) {
          //if we have subsequent nested groupings, sort those based on the ALL row
          sortRow = groupedData[key].find(r =>
            groupProperties.every(g => r[g] === 'All')
          );
        } else {
          sortRow = groupedData[key][0];
        }

        if (sortRow) {
          if (sortFieldType === 'string') {
            return sortRow[columnSort.field].toLowerCase();
          }
          return sortRow[columnSort.field];
        } else {
          return '';
        }
      }
    },
    [columnSort.direction]
  );

  sortedGroupKeys.forEach(sgk => {
    const resultItem = {
      key: sgk,
      value: groupedData[sgk][0]
    };

    if (groupProperties.length > 0) {
      const childSort = getSortGroups(
        groupedData[sgk],
        [...groupProperties],
        columnSort
      );
      resultItem.children = childSort;
    }
    result.push(resultItem);
  });

  return result;
};

export const getSortedGroupRows = (reportData, groupProperties, columnSort) => {
  const sortGroups = getSortGroups(reportData, groupProperties, columnSort);
  return flattenSortGroups(sortGroups);
};

const flattenSortGroups = sortGroups => {
  const result = [];
  sortGroups.forEach(sg => {
    if (sg.children) {
      result.push(...flattenSortGroups(sg.children));
    } else {
      result.push(sg.value);
    }
  });
  return result;
};

export const getGroupsQuery = (groups, aggs) => {
  let query = {};
  groups.reduce((parent, g, i) => {
    const groupAgg = {
      terms: {
        field: g
      }
    };

    parent.aggs = {
      [g.split('.')[g.split('.').length - 2]]: groupAgg,
      ...aggs
    };

    groupAgg.aggs = aggs;

    return groupAgg;
  }, query);

  return query.aggs;
};
